import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { EventNames, EVENTS } from '@app/constants/events.constants';
import { LoginStoreEffects } from '@app/root-store/login-store/effects';
import { RelationshipStoreEffects } from '@app/root-store/relationship-store/effects';
import { VaultStoreEffects } from '@app/root-store/vault-store/effects';
import { SharedConfirmationDialogComponent } from '@app/shared/component/shared-confirmation-dialog/shared-confirmation-dialog.component';
import { UserInviteComponent } from '@app/shared/component/user-invite/user-invite.component';
import { ItemSetupName } from '@app/shared/pipes/item-setup-name.pipe';
import { AuthService, CommonService, DocumentService, UserConnectionService, UserService } from '@app/shared/services';
import { NotifySharingUpdateService } from '@app/shared/services/notify-sharing-update/notify-sharing-update.service';
import { ScriptService } from '@app/shared/services/script/script.service';
import { ShareService } from '@app/shared/services/vault/share.service';
import { stringLocalDateToDatabase } from '@app/shared/utils';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { exit } from 'process';
import { Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import * as selectors from '../../../../root-store/relationship-store/relationship.selector';
import { FileEncryptedSuccessComponent } from '../dialogs/file-encrypted-success/file-encrypted-success.component';
import { AccessManagerBase } from './shared/access-manager.base';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'file-manager',
  templateUrl: './access-manager.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileManagerComponent extends AccessManagerBase implements OnInit, OnDestroy {
  @Input('isStandAloneDialog') isStandAloneDialog = 'true';
  @Input() itemsToSetUpList;
  @Output() parentFun: EventEmitter<any> = new EventEmitter<any>();
  @Output() rlshipOfUserSharedWith: EventEmitter<any> = new EventEmitter<any>();
  @Input() hide_mark_complete = false;
  @Input() ask_mark_complete = false;

  allUser;
  currentUser;
  eventSource = '';
  relationshipObject$;
  relationshipObject;
  isPageLoaded = false;
  allRelationship = [];
  relationshipShared: any[] = [];
  subscription = new Subscription();
  private itemSetupName: ItemSetupName;

  constructor(
    private injector: Injector,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cd: ChangeDetectorRef,
    private authService: AuthService,
    private router: Router,
    private commonService: CommonService,
    private _userService: UserService,
    private userConnectionService: UserConnectionService,
    private relationshipService: RelationshipStoreEffects,
    private vaultShareService: ShareService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private vaultService: VaultStoreEffects,
    private store: Store,
    private loginService: LoginStoreEffects,
    private script: ScriptService,
    documentService: DocumentService,
    _notifySharingUpdateService: NotifySharingUpdateService
  ) {
    super(authService, router, commonService, userConnectionService, dialog, vaultService, script, cd, _notifySharingUpdateService);
    this.title = 'Sharing';
    this.addShowButtonText = 'Share';
    this.currentSectionTitle = 'Shared With';
    this.connectionPlaceholderText = 'Search your network or send an invitation';
    this.emptyExistingUsersText = 'You are not currently sharing this file with any user.';
    this.emptyConnectionText = "You don't have any relationships with whom you can share";
    this.isFromTemplate = true;
    this.showShareButton = true;
    this.showShareTypeOptions = true;
    this.sharedObjectType = 'file';
    this.zerostateTextMobile = 'You are not currently sharing this file with any user.';
    this.dialogName = 'Sharing';
    this.mobileText = 'Search network';

    this.subscription.add(
      this.loginService.loginObject.subscribe((state) => {
        if (state?.data) {
          this.currentUser = state?.data;
        }
      })
    );
    this.itemSetupName = new ItemSetupName();
  }

  async ngOnInit() {
    if (this.inlineComponent && this.nonDialogModalData) {
      this.modalData = { file: this.nonDialogModalData };
    } else {
      this.parentDialogRef = this.injector.get(MatDialogRef<FileManagerComponent>);
      this.modalData = this.data;
    }
    this.eventSource = this.inlineComponent ? 'setup' : 'view';
    this.relationshipObject$ = this.relationshipService.relationshipObject;
    this.subscription.add(
      this.connectionUserFormControl.valueChanges
        .pipe(
          startWith(''),
          map((value) => this.test(value))
        )
        .subscribe((response) => {
          this.users = response;
        })
    );
    if (this.isStandAloneDialog == 'true') {
      this.objectName = this.adminName ? this.adminName : this.modalData.file.form_name;
      this.sharingTitle = this.adminName ? this.adminName : `${this.objectName}`;
      if (this.data?.selectedItemsArray?.length > 0) {
        this.sharingSubtitle = `${
          this.data?.selectedItemsArray.length > 1
            ? ` Manage Sharing and Accessibility for ${this.data?.selectedItemsArray.length} templates.`
            : `Manage Sharing and Accessibility for ${this.data?.selectedItemsArray.length} template.`
        }`;
      } else {
        this.sharingSubtitle = `Manage Sharing and Accessibility for ${this.objectName}`;
      }
    } else {
      this.showHeader = false;
    }
    if (this.data?.ask_mark_complete) {
      this.ask_mark_complete = this.data?.ask_mark_complete;
    }
    if (!this.modalData.file['vault_user_form_id'])
      this.modalData.file['vault_user_form_id'] = this.modalData.file?.currentFormId ?? this.modalData.file?.id;
    if (this.modalData) this.markSharingAsComplete = this.modalData?.markAsCompleteStatus === 'complete' ? true : false;
    await this.getExistingUser();
  }

  async getExistingUser() {
    //get a list of existing users who have been shared this form
    let currentFormId = this.modalData.file?.vault_user_form_id;
    let userId = this.modalData?.file?.user_id ? this.modalData.file.user_id : this.vaultStateObject.userId;
    if (this.inlineComponent) {
      userId = this.currentUser.id;
    }
    if (this.data?.selectedItemsArray?.length > 0) {
      await new Promise<void>((resolve) => {
        this.subscription.add(
          this.vaultShareService
            .getSharedFormUserListByMultipleFormIds(
              this.data.selectedItemsArray.map((x) => x.vault_user_form_id),
              userId
            )
            .subscribe((results) => {
              results.forEach((element, index) => {
                element.name = element?.user_first_name + ' ' + element?.user_last_name;
              });
              this.existingUsers = results;
              this.subscription.add(
                this.store.select(selectors.getrelationshipnameList(this.existingUsers)).subscribe((item) => {
                  this.existingUsers = item;
                  this.allConnection();
                  this.cd.markForCheck();
                })
              );
              resolve();
              this.showLoader = false;
            })
        );
      });
    } else {
      await new Promise<void>((resolve) => {
        this.subscription.add(
          this.vaultShareService.getSharedFormUserList(currentFormId, userId).subscribe((results) => {
            results.forEach((element, index) => {
              element.name = element?.user_first_name + ' ' + element?.user_last_name;
            });
            this.existingUsers = results;
            this.subscription.add(
              this.store.select(selectors.getrelationshipnameList(this.existingUsers)).subscribe((item) => {
                this.existingUsers = item;
                this.allConnection();
                this.cd.markForCheck();
              })
            );
            resolve();
            this.showLoader = false;
          })
        );
      });
    }
  }

  allConnection() {
    this.subscription.add(
      this.store.select(selectors.getAllUserListWithApprovedPending()).subscribe((data) => {
        const finalData = [];
        if (data == null || data == undefined) {
          data = [];
        }
        data.forEach((element) => {
          if (this.existingUsers.findIndex((x) => x.user_id == element.user_id) == -1) {
            var test = JSON.parse(JSON.stringify(element));
            test.id = element?.user_id;
            test.name = element.user_first_name + ' ' + element.user_last_name;
            Object.preventExtensions(test);
            finalData.push(test);
          }
        });
        this.manipulateDate(finalData);
      })
    );
  }

  manipulateDate(data) {
    this.cd.markForCheck();
    this.users = [
      ...data.map(({ id, user_id, name, from_user_id, relationship_name, user_profile_image_url, connection_status }) => ({
        id,
        user_id,
        name,
        from_user_id,
        relationship_name,
        user_profile_image_url,
        connection_status,
      })),
    ];
    this.allUser = [...data];
    this.cd.detectChanges();
  }

  selectConnectedUser(userData) {
    // if ($event.source.selected) {
    this.selectedUser = userData;
    this.isUserSelected = true;
    this.addUserSharewithUser();
    // }
  }

  markSharingAsCompleteEventHandler(event) {
    event.preventDefault();
    this.markSharingAsComplete = !this.markSharingAsComplete;
    let status = this.markSharingAsComplete ? 'complete' : 'created';
    if (this.inlineComponent && this.itemsToSetUpList) {
      let itemsToSetUp = this.itemsToSetUpList.find((x) => x.type == 'share');
      let transformedItemSetupName = this.itemSetupName.transform(itemsToSetUp);
      if (itemsToSetUp) {
        this.subscription.add(
          this.documentService
            .updateItemsToSetUpList({
              id: itemsToSetUp.id,
              status: status,
              setupLabel: transformedItemSetupName,
            })
            .subscribe((res) => {
              this.markSharingAsComplete = res.status;
              this.parentFun.emit({
                status: res.status,
                type: 'share',
              });
              this.cd.detectChanges();
            })
        );
      }
    } else {
      this._notifySharingUpdateService.sendData({ status: status });
    }
  }

  test(value) {
    if (this.allUser) {
      return this.allUser.filter((user) => user.full_name.toLowerCase().includes(value.toLowerCase()));
    }
  }

  Invite() {
    this.isPageLoaded = true;
    const data = {
      disableSelectionOption: true,
      custom_user_type: 'normal',
    };
    const modalRef = this.dialog.open(UserInviteComponent, {
      autoFocus: false,
      data: data,
      panelClass: 'full-window-dialog-2-mobile-responsive',
    });
    this.subscription.add(
      modalRef.afterClosed().subscribe((res) => {
        if (res?.success && res?.isexistingUser) {
          this.subscription.add(
            this.relationshipObject$.subscribe((data) => {
              this.relationshipObject = data;
              this.allRelationship = data['allRelationships'];
              if (this.allRelationship != null && this.allRelationship.length && this.isPageLoaded) {
                let index = this.allRelationship.findIndex((x) => x.to_user_id == res.user['to_user_id']);
                if (index != -1) {
                  this.selectedUser = this.allRelationship[index];
                  this.isUserSelected = true;
                  this.isPageLoaded = false;
                  this.addUserSharewithUser();
                }
              }
            })
          );
        } else if (res?.success && !res?.isexistingUser) {
          this.subscription.add(
            this.relationshipObject$.subscribe((data) => {
              this.relationshipObject = data;
              this.allRelationship = data['allRelationships'];
              if (this.allRelationship != null && this.allRelationship.length && this.isPageLoaded) {
                let index = this.allRelationship.findIndex((x) => x.to_user_id == res.user['id']);
                if (index != -1) {
                  this.selectedUser = this.allRelationship[index];
                  this.isUserSelected = true;
                  this.isPageLoaded = false;
                  this.addUserSharewithUser();
                }
              }
            })
          );
        }
        this.cd.detectChanges();
      })
    );
  }

  async addUserSharewithUser() {
    let didCancel = false;
    await new Promise<void>((resolve, error) => {
      this.launchNewShareOptionsDialog(
        () => {
          resolve();
        },
        () => {
          error();
        }
      );
    }).catch(() => {
      didCancel = true;
    });

    if (didCancel) {
      //clear selection from mat auto complete
      this.clearAutoCompleteValue();

      exit;
    }

    if (!this.newShareTypeDetails) {
      //if no options have been selected set defaults
      this.newShareTypeDetails = { share_date: new Date(Date.now()).toLocaleDateString(), is_sealed: false };
    }
    if (this.data?.selectedItemsArray?.length > 0) {
      this.subscription.add(
        this.vaultShareService
          .shareFormMultipleUser({
            connection_status: this.selectedUser.connection_status,
            user_id: this.modalData?.file?.user_id ? this.modalData.file.user_id : this.vaultStateObject.userId,
            shared_with_user_id: this.selectedUser.user_id,
            vault_user_form_id_list: this.data?.selectedItemsArray.map((x) => x.vault_user_form_id),
            end_date: this.newShareTypeDetails.end_date._i ? stringLocalDateToDatabase(this.newShareTypeDetails.end_date) : null,
            share_date: this.newShareTypeDetails.share_date ? stringLocalDateToDatabase(this.newShareTypeDetails.share_date) : null,
            is_sealed: this.newShareTypeDetails.is_sealed,
            vault_user_form_id: this.modalData.file.vault_user_form_id,
            is_editable: this.newShareTypeDetails.is_editable,
          })
          .subscribe(async (results) => {
            await this.getExistingUser();
            //this.getAllConnections();

            //clear selection from mat auto complete
            this.clearAutoCompleteValue();

            //refresh modal
            this.cd.detectChanges();
          })
      );
    } else {
      this.subscription.add(
        this.vaultShareService
          .shareForm({
            connection_status: this.selectedUser.connection_status,
            user_id: this.modalData?.file?.user_id ? this.modalData.file.user_id : this.vaultStateObject.userId,
            shared_with_user_id: this.selectedUser.user_id,
            end_date: this.newShareTypeDetails.end_date._i ? stringLocalDateToDatabase(this.newShareTypeDetails.end_date) : null,
            share_date: this.newShareTypeDetails.share_date ? stringLocalDateToDatabase(this.newShareTypeDetails.share_date) : null,
            is_sealed: this.newShareTypeDetails.is_sealed,
            vault_user_form_id: this.modalData.file.vault_user_form_id,
            is_editable: this.newShareTypeDetails.is_editable,
          })
          .subscribe(async (results) => {
            if (results?.securityKey) {
              this.dialog.open(FileEncryptedSuccessComponent, {});
            }
            this.toastr.success(` ${this.modalData.file.form_name} File shared with   ${this.selectedUser?.name}.`);
            this.trackEventsInMixPanel([
              {
                eventName: EVENTS[`template_${this.eventSource}_add_share`],
                data: { shared_form_name: this.modalData.file.form_name, shared_with_user_name: this.selectedUser?.name },
              },
            ]);
            await this.getExistingUser();
            //this.getAllConnections();

            //clear selection from mat auto complete
            this.clearAutoCompleteValue();

            //refresh modal
            this.cd.detectChanges();
          })
      );
    }

    this.rlshipOfUserSharedWith.emit(this.relationshipShared);
  }

  deleteExistingUser(existingUser) {
    const sharedDialogRef = this.dialog.open(SharedConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete',
        materialIcon: 'delete',
        text: `Are you sure you want to delete this user's access to this File?`,
        proceedTitle: 'Delete',
      },
    });

    this.subscription.add(
      sharedDialogRef.afterClosed().subscribe((confirmation) => {
        if (confirmation) {
          this.subscription.add(
            this.vaultShareService
              .deleteFormShare({
                user_id: this.modalData?.file?.user_id ? this.modalData.file.user_id : this.vaultStateObject.userId,
                id: existingUser.vault_user_share_id,
              })
              .subscribe((results) => {
                this.toastr.success(`${existingUser.user_first_name}'s access removed for ${this.modalData.file.form_name} File `);
                this.trackEventsInMixPanel([
                  {
                    eventName: EVENTS[`template_${this.eventSource}_remove_share`],
                    data: { shared_form_name: this.modalData.file.form_name, shared_with_user_name: existingUser.user_first_name },
                  },
                ]);
                this.getExistingUser();
                //this.getAllConnections();

                //refresh modal
                this.cd.markForCheck();
              })
          );
        }
      })
    );
  }

  updateExistingUser(value, existingUser) {
    if (existingUser?.vault_user_share_id_list?.length) {
      this.subscription.add(
        this.vaultShareService
          .updateFormShareMultiple({
            user_id: this.modalData?.file?.user_id ? this.modalData.file.user_id : this.vaultStateObject.userId,
            vault_user_share_ids: existingUser.vault_user_share_id_list,
            share_date: stringLocalDateToDatabase(value.share_date),
            is_sealed: value.is_sealed,
            is_editable: value.is_editable,
            end_date: value.end_date._i ? stringLocalDateToDatabase(value.end_date) : null,
          })
          .subscribe(async (results) => {
            this.toastr.success(`${existingUser.user_first_name}'s access updated for ${this.modalData.file.form_name} File `);
            this.trackEventsInMixPanel([
              {
                eventName: EVENTS[`template_${this.eventSource}_edit_share`],
                data: { shared_form_name: this.modalData.file.form_name, shared_with_user_name: existingUser.user_first_name },
              },
            ]);
            await this.getExistingUser();
            //refresh modal
            this.cd.markForCheck();
          })
      );
    } else {
      this.subscription.add(
        this.vaultShareService
          .updateFormShare({
            user_id: this.modalData?.file?.user_id ? this.modalData.file.user_id : this.vaultStateObject.userId,
            id: existingUser.vault_user_share_id,
            share_date: stringLocalDateToDatabase(value.share_date),
            is_sealed: value.is_sealed,
            is_editable: value.is_editable,
            end_date: value.end_date._i ? stringLocalDateToDatabase(value.end_date) : null,
          })
          .subscribe(async (results) => {
            this.toastr.success(`${existingUser.user_first_name}'s access updated for ${this.modalData.file.form_name} File `);
            this.trackEventsInMixPanel([
              {
                eventName: EVENTS[`template_${this.eventSource}_edit_share`],
                data: { shared_form_name: this.modalData.file.form_name, shared_with_user_name: existingUser.user_first_name },
              },
            ]);
            await this.getExistingUser();
            //refresh modal
            this.cd.markForCheck();
          })
      );
    }
  }

  trackEventsInMixPanel(events: { eventName: EventNames; data?: any }[]) {
    this._userService.trackEventsInMixPanel(events).then((response) => {});
  }

  shareTypeUpdated(value) {
    this.newShareTypeDetails = value;
  }

  setShareDetails(existingUser) {
    return {
      ...existingUser,
      end_date: existingUser.vault_user_share_end_date,
      share_date: existingUser.vault_user_share_share_date,
      is_sealed: existingUser.vault_user_share_is_sealed,
      is_editable: existingUser.vault_user_share_is_editable,
    };
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.isPageLoaded = false;
    this._userService.cleanupWorker();
  }
}
